import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Menu, MenuItem } from '@mui/material'
import { ReactElement, useState } from 'react'
import { Checkbox } from 'src/features/partsCatalog/Selections/components/Checkbox'
import { I18n } from 'src/i18n'
import { GroupPart } from 'src/store/lists/interfaces'
import { defaultTheme } from 'src/theme'
import styled from 'styled-components'
import { SelectionOption } from './interfaces'

interface Props {
  selectionOption: SelectionOption
  pageItems: GroupPart[]
  groupItemsCount: number
  showClearSelections: boolean
  selectPageItems: () => void
  selectGroupItems: () => void
  clearSelections: () => void
}

const SelectPartsMenu = (props: Props): ReactElement => {
  const {
    selectionOption,
    pageItems,
    groupItemsCount,
    selectPageItems,
    selectGroupItems,
    clearSelections,
    showClearSelections,
  } = props
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = Boolean(menuAnchorEl)

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleOptionChange = async (option: SelectionOption) => {
    setMenuAnchorEl(null)
    await new Promise<void>((resolve) => setTimeout(() => resolve(), 100)) // avoiding clearSelections be visible before closing the menu

    if (option === SelectionOption.FULL_PAGE) selectPageItems()
    if (option === SelectionOption.FULL_GROUP) selectGroupItems()
    if (option === SelectionOption.NONE) clearSelections()
  }

  const isChecked = [
    SelectionOption.FULL_PAGE,
    SelectionOption.FULL_GROUP,
  ].includes(selectionOption)

  return (
    <Wrapper>
      <Flex onClick={handleMenuClick} selected={openMenu}>
        <Checkbox value="" label="" isCheckMixed={isChecked} />
        {!openMenu && <StyledExpandMore />}
        {openMenu && <StyledExpandLess />}
      </Flex>
      <StyledMenu
        anchorEl={menuAnchorEl}
        open={openMenu}
        onClose={handleMenuClose}
      >
        <Items>
          <Item
            selected={selectionOption === SelectionOption.FULL_PAGE}
            onClick={() => handleOptionChange(SelectionOption.FULL_PAGE)}
          >
            <I18n
              name="selectXItemsOnPage"
              textReplacement={[pageItems?.length]}
            />
          </Item>
          <Item
            selected={selectionOption === SelectionOption.FULL_GROUP}
            onClick={() => handleOptionChange(SelectionOption.FULL_GROUP)}
          >
            <I18n
              name="selectAllXItemsInGroup"
              textReplacement={[groupItemsCount]}
            />
          </Item>
          {!!showClearSelections && (
            <Item onClick={() => handleOptionChange(SelectionOption.NONE)}>
              <I18n name="clearSelections" />
            </Item>
          )}
        </Items>
      </StyledMenu>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: fit-content;
  margin-right: 16px;
  cursor: pointer;
  padding-left: 22px;
`

interface FlexProps {
  selected: boolean
}

const Flex = styled.div<FlexProps>`
  display: flex;
  background: ${(p) =>
    p.selected ? defaultTheme.colors.greyE5E5E5 : 'inherit'};
  border-radius: 4px;
  align-items: center;
  padding: 0.2rem;
`

const StyledMenu = styled(Menu)``

const StyledExpandMore = styled(ExpandMore)`
  color: ${defaultTheme.colors.columnBorder};
`

const StyledExpandLess = styled(ExpandLess)`
  color: ${defaultTheme.colors.columnBorder};
`

const Items = styled.div`
  width: 217px;
`

interface ItemProps {
  selected?: boolean
}

const Item = styled(MenuItem)<ItemProps>`
  display: flex;
  gap: 0.3rem;
  text-transform: Capitalize;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  background: ${(p) =>
    p.selected ? `${defaultTheme.colors.primary}!important` : 'inherit'};
`

export default SelectPartsMenu
